import { z } from 'zod';

import { HAS_SIGNED_IN_KEY } from '@/constants/authConstants.ts';
import { IdentitySchema, UserInfoSchema } from '@/types/schema';
import { useLocalStorageState } from '@/utils/use-local-storage-state.tsx';

const LastAuthProviderSchema = z.union([
  z.literal('github'),
  z.literal('google'),
  z.literal('discord'),
  z.literal('email'),
  z.null(),
]);

export type LastAuthProvider = z.infer<typeof LastAuthProviderSchema>;

export function useLastActiveProvider() {
  const [lastActive] = useLocalStorageState(
    'ls:auth:lastAuthProvider',
    null,
    LastAuthProviderSchema.nullable()
  );
  const hasSignedIn = localStorage.getItem(HAS_SIGNED_IN_KEY);
  return { lastActive: hasSignedIn ? lastActive : null, hasSignedIn };
}

// We are reusing useLocalStorageState to ensure that our
// LangSmith-specific localStorage keys are properly namespaced.
// Separating read hook from update hook
// prevents the "Last used" tag from being automatically
// updated when the user clicks on a different social login provider.
export function useLastActiveProviderUpdate() {
  const [, setLastActive] = useLocalStorageState(
    'ls:auth:lastAuthProvider',
    null,
    LastAuthProviderSchema.nullable()
  );
  return setLastActive;
}

export function matchingUser(
  users: UserInfoSchema[] | undefined,
  user_id: string | undefined
): UserInfoSchema | undefined {
  if (!user_id || !users) {
    return undefined;
  }
  return users?.find(
    (u) => u.user_id === user_id || u.linked_user_ids.includes(user_id ?? '')
  );
}

export function userInfosFromMembers(
  members: IdentitySchema[]
): UserInfoSchema[] {
  return members.map((m, i) => ({
    name: m.full_name ?? m.email ?? `User ${i}`,
    user_id: m.user_id,
    linked_user_ids:
      m.linked_login_methods?.map((login) => login.provider_user_id ?? '') ??
      [],
  }));
}
